import React from 'react';
import { Compass, Target, HeartHandshake, Sparkles, Building2, Globe } from 'lucide-react';
import MarketingPage, { ContentCard, ContactCallout } from '@/components/marketing/MarketingPage';
import { pageJsonLd } from '@/components/shared/Seo';

const JSONLD = pageJsonLd({
  type: 'AboutPage',
  name: 'About Numuv',
  description: 'Numuv\u2019s mission is to converge the applicant, staffing partner and hiring team journeys into one AI-native recruitment system.',
  path: '/about',
  breadcrumbs: [{ name: 'About', path: '/about' }],
});

export default function About() {
  return (
    <MarketingPage
      jsonLd={JSONLD}
      seoTitle="About Numuv — building the future of AI recruitment"
      seoDescription="Learn about Numuv's mission to unify applicants, staffing partners, and hiring teams on one AI-native recruitment platform. Meet the team, our values, and how we hire."
      seoPath="/about"
      seoKeywords="about Numuv, AI recruitment company, hiring platform mission, Numuv team"
      eyebrow="Our story"
      title="Recruitment, reimagined as one living system"
      subtitle="Numuv exists because hiring today is fractured — three parties (applicants, staffing partners, hiring teams) each stuck in their own tools. We believe AI can converge these journeys into one intelligent flow."
    >
      <div className="grid md:grid-cols-3 gap-5 md:gap-7 mt-4">
        <ContentCard>
          <span className="grid h-12 w-12 place-items-center rounded-[14px] bg-[#6D5EF7]/12 text-[#6D5EF7]"><Compass className="w-5 h-5" /></span>
          <h3 className="dis mt-5 text-[19px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>Our mission</h3>
          <p className="mt-2 text-[14px] leading-[22px] t3">Give every candidate a fair shot and every team the intelligence to hire faster — without bias, guesswork, or spreadsheet chaos.</p>
        </ContentCard>
        <ContentCard>
          <span className="grid h-12 w-12 place-items-center rounded-[14px] bg-[#12B6A6]/12 text-[#12B6A6]"><Target className="w-5 h-5" /></span>
          <h3 className="dis mt-5 text-[19px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>What we build</h3>
          <p className="mt-2 text-[14px] leading-[22px] t3">Vector-powered matching, autonomous AI interviews with Jay, and end-to-end pipeline automation — designed for applicants, staffing organisations, and hiring teams.</p>
        </ContentCard>
        <ContentCard>
          <span className="grid h-12 w-12 place-items-center rounded-[14px] bg-[#8B5CF6]/12 text-[#8B5CF6]"><HeartHandshake className="w-5 h-5" /></span>
          <h3 className="dis mt-5 text-[19px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>How we work</h3>
          <p className="mt-2 text-[14px] leading-[22px] t3">Ship fast, listen to users, iterate weekly. Numuv is opinionated where it matters and flexible everywhere else.</p>
        </ContentCard>
      </div>

      <ContentCard className="mt-8">
        <div className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-[#6D5EF7]">
          <Sparkles className="w-4 h-4" /> Principles
        </div>
        <div className="mt-4 grid md:grid-cols-2 gap-6">
          {[
            { t: 'Candidates first', d: 'Every feature is validated against candidate experience — the resume is a starting point, not a filter.' },
            { t: 'Bias-aware AI', d: 'Every model output is auditable. We highlight signals, not verdicts, so humans always own the decision.' },
            { t: 'One platform, three journeys', d: 'We refuse to build for just one role. Applicants, staffing partners, and hiring teams share the same intelligence.' },
            { t: 'Fair pricing (Mu Points)', d: 'You pay only for the AI actions you actually consume. No seat traps, no forced enterprise plans.' },
          ].map((p) => (
            <div key={p.t}>
              <p className="dis text-[15.5px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>{p.t}</p>
              <p className="mt-1 text-[14px] leading-[22px] t3">{p.d}</p>
            </div>
          ))}
        </div>
      </ContentCard>

      <ContentCard className="mt-8" data-testid="about-parent-org-card">
        <div className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-[#12B6A6]">
          <Building2 className="w-4 h-4" /> Company & Legal Entity
        </div>
        <h3 className="dis mt-4 text-[19px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }} data-testid="about-parent-org-name">
          Numuv is a product of Mu Technology Labs Private Limited
        </h3>
        <p className="mt-2 text-[14px] leading-[22px] t3">
          Numuv is owned, developed, and operated by <strong className="t1">Mu Technology Labs Private Limited</strong>, a company registered in India. All Numuv services, communications, and business messaging — including our official WhatsApp Business account under the display name <strong className="t1">Numuv</strong> — are provided by Mu Technology Labs Private Limited.
        </p>
        <a
          href="https://www.mutech.co.in"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center gap-2 text-[14px] font-medium text-[#6D5EF7] hover:underline"
          data-testid="about-parent-org-website"
        >
          <Globe className="w-4 h-4" /> www.mutech.co.in
        </a>
      </ContentCard>

      <div className="mt-10 text-center">
        <p className="t2 text-[14.5px]">Questions or partnership ideas?</p>
        <div className="mt-3 flex justify-center"><ContactCallout /></div>
      </div>
    </MarketingPage>
  );
}
